import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import Button from "../../components/Button";
import SallieReferral from "./components/SallieReferral";

// import { submitAnswers } from "./api/submitRequest";

const decisionTree = {
    headhit_Front_of_Head: {
        question: "Did the person hit the front of their head?",
        yes: "headhit_Back",
        no: "imm_symp_Incoherent_Speech_1",
    },
    imm_symp_Incoherent_Speech_1: {
        question: "Does the person experience incoherent speech?",
        yes: "headhit_Back_of_Head",
        no: "headhit_Back_left",
    },
    headhit_Back_left: {
        question: "Did the person hit the back of their body?",
        yes: "imm_symp_Confusion_2",
        no: "imm_symp_Confusion_1",
    },
    imm_symp_Confusion_1: {
        question: "Does the person experience confusion?",
        yes: "end_0_278",
        no: "end_0_2251",
    },
    imm_symp_Confusion_2: {
        question: "Does the person experience confusion?",
        yes: "end_0_3286",
        no: "end_0_2668",
    },
    headhit_Back_of_Head: {
        question: "Did the person hit the back of their head?",
        yes: "end_0_3614",
        no: "imm_symp_Dazed_or_Vacant_Stare",
    },
    imm_symp_Dazed_or_Vacant_Stare: {
        question: "Does the person have a dazed or vacant stare?",
        yes: "end_0_3265",
        no: "end_0_2907",
    },
    headhit_Back: {
        question: "Did the person hit the back of their body?",
        yes: "imm_symp_Incoherent_Speech_2",
        no: "imm_symp_Memory_Loss",
    },
    imm_symp_Memory_Loss: {
        question: "Does the person experience memory loss?",
        yes: "imm_symp_Incoherent_Speech_3",
        no: "end_0_2671",
    },
    imm_symp_Incoherent_Speech_3: {
        question: "Does the person experience incoherent speech?",
        yes: "end_0_3709",
        no: "end_0_3244",
    },
    imm_symp_Incoherent_Speech_2: {
        question: "Does the person experience incoherent speech?",
        yes: "headhit_Top_of_Head",
        no: "end_0_3952",
    },
    headhit_Top_of_Head: {
        question: "Did the person hit the top of their head?",
        yes: "end_0_4674",
        no: "end_0_4197",
    },
    // End nodes (predictions)
    end_0_3614: { prediction: "0.3614", decision: "Pull" },
    end_0_2671: { prediction: "0.2671", decision: "Play" },
    end_0_278: { prediction: "0.278", decision: "Play" },
    end_0_2251: { prediction: "0.2251", decision: "Play" },
    end_0_3952: { prediction: "0.3952", decision: "Pull" },
    end_0_4674: { prediction: "0.4674", decision: "Pull" },
    end_0_4197: { prediction: "0.4197", decision: "Pull" },
    end_0_2907: { prediction: "0.2907", decision: "Play" },
    end_0_3265: { prediction: "0.3265", decision: "Pull" },
    end_0_3709: { prediction: "0.3709", decision: "Pull" },
    end_0_3244: { prediction: "0.3244", decision: "Pull" },
    end_0_2668: { prediction: "0.2668", decision: "Play" },
    end_0_3286: { prediction: "0.3286", decision: "Pull" },
};

function useIISSFlow() {
    const [currentNode, setCurrentNode] = useState("headhit_Front_of_Head");
    const [answers, setAnswers] = useState({});
    const [score, setScore] = useState(null);
    const [decision, setDecision] = useState(null);

    useEffect(() => {
        if (score !== null && decision !== null) {
            const payload = {
                userId: localStorage.getItem("userId"),
                answers,
                score,
                decision: decision === "Play",
            };
            console.log("Submit payload:", payload);
        }
    }, [score, decision, answers]);

    const handleAnswer = (answer) => {
        const nextNode = decisionTree[currentNode][answer];
        const updated = { ...answers, [currentNode]: answer === "yes" };
        setAnswers(updated);

        if (nextNode.startsWith("end_")) {
            const { prediction, decision: dec } = decisionTree[nextNode];
            setScore(prediction);
            setDecision(dec);
        } else {
            setCurrentNode(nextNode);
        }
    };

    const restart = () => {
        setCurrentNode("headhit_Front_of_Head");
        setAnswers({});
        setScore(null);
        setDecision(null);
    };

    return { currentNode, answers, score, decision, handleAnswer, restart };
}

const IISSDecisionFlow = () => {
    const navigate = useNavigate();
    const { currentNode, answers, score, decision, handleAnswer, restart } =
        useIISSFlow();

    const renderHistory = () =>
        Object.entries(answers).map(([key, val]) => (
            <div key={key} className="flex justify-between mb-2">
                <p>{decisionTree[key].question}</p>
                <p
                    className={`font-semibold ${
                        val ? "text-green-500" : "text-red-500"
                    }`}
                >
                    {val ? "Yes" : "No"}
                </p>
            </div>
        ));

    return (
        <div className="w-4/5 md:w-2/3 lg:w-1/3 mx-auto">
            <h3 className="text-lg font-bold text-center md:text-2xl md:mb-4">
                Injury Impact Severity Score
            </h3>
            <h6
                className="text-center cursor-pointer text-teal-600 mb-4"
                onClick={() => navigate("/")}
            >
                Logout
            </h6>
            <div className="mb-6">{renderHistory()}</div>

            <div className="custom-container">
                {score == null ? (
                    <>
                        <p className="mb-4 font-semibold text-base text-center md:text-lg">
                            Q. {decisionTree[currentNode].question}
                        </p>
                        <div className="flex gap-4 justify-center">
                            <Button onClick={() => handleAnswer("yes")}>
                                Yes
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => handleAnswer("no")}
                            >
                                No
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex flex-col">
                            <p className="text-base font-semibold md:text-lg">
                                Recommendation:
                            </p>
                            <p
                                className={`font-bold text-lg md:text-2xl ${
                                    decision === "Pull"
                                        ? "text-red-600"
                                        : "text-green-600"
                                }`}
                            >
                                {decision === "Pull"
                                    ? "Refer for Further Examination"
                                    : "Refer to Sallie for Resources"}
                            </p>
                        </div>
                        <div
                            className={`
								flex items-center justify-center mx-auto w-32 h-32 mt-6 rounded-full border text-3xl text-white font-bold mb-6
								${decision === "Pull" ? "bg-red-500" : "bg-green-500"}`}
                        >
                            {score}
                        </div>
                        <SallieReferral />
                    </>
                )}
            </div>

            <Button
                variant="outline"
                onClick={restart}
                className="mx-auto mt-4"
            >
                Restart
            </Button>
        </div>
    );
};

export default IISSDecisionFlow;
